import { ApolloError } from '@apollo/client'
import { MergeGuestSessionMutation } from '__generated__/graphql'
import { NotificationState } from 'features/notification/types'
import type { GuestDemoRounds } from '../store'

type ClaimResult = MergeGuestSessionMutation['mergeGuestSession']
type Mode = keyof GuestDemoRounds
type ModeResult = Pick<
  ClaimResult[Mode],
  'roundsMigrated' | 'scoreMigrated' | 'rankDaily'
>

export type ClaimToast = Pick<
  NotificationState,
  'type' | 'title' | 'description'
>

const MODE_LABELS: Record<Mode, string> = {
  upDownAi: 'Human vs AI',
  bullseye: "Bull's Eye",
  upDown: 'Up/Down'
}

const MODES = Object.keys(MODE_LABELS) as Mode[]

/**
 * `GuestSessionAlreadyClaimedException` of the users service (HTTP 409): the
 * guest session belongs to another user, or this user has a session already.
 */
const ALREADY_CLAIMED_MESSAGES = [
  'Guest session already claimed',
  'User already claimed a guest session'
]
const CONFLICT = 409

const plural = (count: number, noun: string) =>
  `${count} ${noun}${count === 1 ? '' : 's'}`

const formatScore = (score: number) =>
  `${score > 0 ? '+' : ''}${Number.isInteger(score) ? score : score.toFixed(1)}`

export const countRounds = (rounds: GuestDemoRounds) =>
  MODES.reduce((sum, mode) => sum + rounds[mode], 0)

/** "Human vs AI: 3 rounds · Bull's Eye: 1 round"; empty when nothing was played. */
export const describeDemoRounds = (rounds: GuestDemoRounds) =>
  MODES.filter(mode => rounds[mode] > 0)
    .map(mode => `${MODE_LABELS[mode]}: ${plural(rounds[mode], 'round')}`)
    .join(' · ')

const describeMode = (label: string, mode: ModeResult) => {
  if (mode.roundsMigrated === 0) return null

  return [
    `${label}: ${plural(mode.roundsMigrated, 'round')}`,
    mode.scoreMigrated ? `score ${formatScore(mode.scoreMigrated)}` : null,
    mode.rankDaily != null ? `daily rank #${mode.rankDaily}` : null
  ]
    .filter(Boolean)
    .join(', ')
}

/**
 * What the toast says after a claim. The result counts only the rounds that
 * reached a leaderboard, so one short of the rounds the guest played is a
 * partial success: rounds still in play are credited by the backend once
 * they settle, the reasons for the rest arrive with BE-312.
 */
export const describeClaimResult = (
  result: ClaimResult,
  rounds: GuestDemoRounds
): ClaimToast => {
  const played = countRounds(rounds)
  const isPartial = played > 0 && result.roundsMigrated < played
  const credited = MODES.map(mode =>
    describeMode(MODE_LABELS[mode], result[mode])
  )
    .filter(Boolean)
    .join(' · ')

  if (!isPartial) {
    return {
      type: 'success',
      title: 'Demo results claimed',
      description: credited || 'Your demo results are on the leaderboards.'
    }
  }

  return {
    type: 'warning',
    title: `Demo results partly claimed: ${result.roundsMigrated} of ${played} rounds`,
    description: [
      credited,
      'Rounds still in play are credited once they settle; rejected or expired rounds are not.'
    ]
      .filter(Boolean)
      .join('. ')
  }
}

/** The claim was refused for good (409): retrying cannot help. */
export const ALREADY_CLAIMED_TOAST: ClaimToast = {
  type: 'info',
  title: 'Demo results not claimed',
  description:
    'This guest session belongs to another account, or yours already has demo results of its own.'
}

// NOTE: production keeps only the message (`extensions.code` BAD_REQUEST);
// other stands also pass the HTTP status
export const isAlreadyClaimedError = (error: unknown) =>
  error instanceof ApolloError &&
  error.graphQLErrors.some(
    ({ message, extensions }) =>
      ALREADY_CLAIMED_MESSAGES.includes(message) ||
      extensions?.httpStatus === CONFLICT
  )

export const getClaimErrorMessage = (error: unknown) => {
  const message =
    error instanceof ApolloError ?
      (error.graphQLErrors[0]?.message ?? error.message)
    : error instanceof Error ? error.message
    : ''

  return message || 'Something went wrong. Your demo results are kept.'
}
